import React, { useState } from 'react';
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, CircularProgress, ButtonGroup, Button,
  Drawer, useMediaQuery, useTheme, Stack, TablePagination
} from '@mui/material';
import { FilterList, Refresh } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

import { useNotificationLogs } from '../hooks/useNotificationLogs';
import { COLORS } from './Constants';
import FiltersSidebar from './FiltersSidebar';
import NotificationLogRow from './NotificationLogRow';
import MobileCard from './MobileCard';

const {
  backgroundColorDarkTheme,
  tableBackgroundColor,
  tableHeaderColor,
  borderColorDarkTheme,
  fontColorDarkTheme
} = COLORS;

const TABLE_COLUMNS = [
  { labelKey: 'notificationLogs.table.dateTime', width: '20%' },
  { labelKey: 'notificationLogs.table.platform', width: '20%' },
  { labelKey: 'notificationLogs.table.status', width: '12%' },
  { labelKey: 'notificationLogs.table.subject', width: '38%' },
  { labelKey: 'notificationLogs.table.details', width: '10%', align: 'right' as const }
];

const NotificationLogsList = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  const { state, actions } = useNotificationLogs();

  return (
    <Box sx={{ display: 'flex', height: 'calc(100vh - 64px)', overflow: 'hidden', backgroundColor: backgroundColorDarkTheme, color: fontColorDarkTheme }}>
      
      {isMobile ? (
        <Drawer
          anchor="left"
          open={isFilterOpen}
          onClose={() => setIsFilterOpen(false)}
          slotProps={{ paper: { sx: { width: 260, backgroundColor: tableHeaderColor } } }}
        >
          <FiltersSidebar state={state.filters} actions={actions} validation={state.validation} isLoading={state.isLoading || state.isFetching} />
        </Drawer>
      ) : (
        <Box sx={{ width: 260, flexShrink: 0, borderRight: `1px solid ${borderColorDarkTheme}`, backgroundColor: tableHeaderColor }}>
          <FiltersSidebar state={state.filters} actions={actions} validation={state.validation} isLoading={state.isLoading || state.isFetching} />
        </Box>
      )}

      <Box sx={{ flex: 1, p: 4, overflowY: 'auto' }}>
        <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', mb: 3, gap: 2, flexWrap: 'wrap' }}>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 0.5 }}>{t('notificationLogs.title')}</Typography>
              <Typography variant="body2" sx={{ color: 'gray' }}>{t('notificationLogs.description')}</Typography>
            </Box>

            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
              {isMobile && (
                <Button variant="outlined" startIcon={<FilterList />} onClick={() => setIsFilterOpen(true)} sx={{ textTransform: 'none', borderColor: borderColorDarkTheme, color: 'gray' }}>
                  {t('notificationLogs.filters.button')}
                </Button>
              )}

              <ButtonGroup variant="outlined" size="small" sx={{ '& .MuiButton-root': { borderColor: borderColorDarkTheme, color: 'gray', textTransform: 'none' } }}>
                <Button onClick={() => actions.handleQuickDateFilter('hoje')} sx={{ backgroundColor: state.filters.activeQuickFilter === 'hoje' ? 'rgba(88, 166, 255, 0.1)' : 'transparent', color: state.filters.activeQuickFilter === 'hoje' ? '#58a6ff !important' : 'inherit' }}>{t('notificationLogs.quickFilters.today')}</Button>
                <Button onClick={() => actions.handleQuickDateFilter('7d')} sx={{ backgroundColor: state.filters.activeQuickFilter === '7d' ? 'rgba(88, 166, 255, 0.1)' : 'transparent', color: state.filters.activeQuickFilter === '7d' ? '#58a6ff !important' : 'inherit' }}>{t('notificationLogs.quickFilters.last7Days')}</Button>
                <Button onClick={() => actions.handleQuickDateFilter('todos')} sx={{ backgroundColor: state.filters.activeQuickFilter === 'todos' ? 'rgba(88, 166, 255, 0.1)' : 'transparent', color: state.filters.activeQuickFilter === 'todos' ? '#58a6ff !important' : 'inherit' }}>{t('notificationLogs.quickFilters.all')}</Button>
              </ButtonGroup>

              <Button variant="contained" startIcon={<Refresh />} onClick={actions.handleRefresh} disabled={state.isFetching} sx={{ textTransform: 'none' }}>
                {t('notificationLogs.actions.refresh')}
              </Button>
            </Box>
          </Box>

          {isMobile ? (
            <>
              <Stack spacing={2}>
                {state.isLoading || state.isFetching ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={30} sx={{ color: fontColorDarkTheme }} /></Box>
                ) : state.logs.length === 0 ? (
                  <Typography sx={{ color: fontColorDarkTheme }}>{t('notificationLogs.table.noData')}</Typography>
                ) : (
                  state.logs.map((log) => <MobileCard key={log.id} row={log} />)
                )}
              </Stack>
              <PaginationFooter state={state} actions={actions} />
            </>
          ) : (
            <TableContainer component={Paper} sx={{ backgroundColor: tableBackgroundColor, borderRadius: 2, boxShadow: '0 4px 24px rgba(0,0,0,0.25)', border: `1px solid ${borderColorDarkTheme}`, overflowX: 'auto' }}>
              <Table size="small" sx={{ tableLayout: 'fixed', width: '100%', minWidth: 700, borderCollapse: 'collapse' }}>
                <TableHead>
                  <TableRow>
                    {TABLE_COLUMNS.map((col, index) => (
                      <TableCell key={index} align={col.align || 'left'} sx={{ width: col.width, backgroundColor: tableHeaderColor, color: 'gray', fontWeight: 'bold', borderBottom: `1px solid rgba(255, 255, 255, 0.1)`, borderRight: 'none' }}>
                        {t(col.labelKey)}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {state.isLoading || state.isFetching ? (
                    <TableRow><TableCell colSpan={5} align="center" sx={{ py: 5, borderBottom: 'none' }}><CircularProgress size={30} sx={{ color: fontColorDarkTheme }} /></TableCell></TableRow>
                  ) : state.logs.length === 0 ? (
                    <TableRow><TableCell colSpan={5} align="center" sx={{ py: 5, color: fontColorDarkTheme, borderBottom: 'none' }}>{t('notificationLogs.table.noData')}</TableCell></TableRow>
                  ) : (
                    state.logs.map((log) => <NotificationLogRow key={log.id} row={log} />)
                  )}
                </TableBody>
              </Table>
              <PaginationFooter state={state} actions={actions} />
            </TableContainer>
          )}

        </Box>
      </Box>

    </Box>
  );
};

type NotificationLogsHookReturn = ReturnType<typeof useNotificationLogs>;
type PaginationFooterProps = Pick<NotificationLogsHookReturn, 'state' | 'actions'>;

const PaginationFooter = ({ state, actions }: PaginationFooterProps) => {
  const { t } = useTranslation();

  return (
    <TablePagination
      component="div"
      count={state.totalCount}
      page={state.pagination.pageNumber - 1}
      onPageChange={actions.handleChangePage}
      rowsPerPage={state.pagination.pageSize}
      onRowsPerPageChange={actions.handleChangeRowsPerPage}
      labelRowsPerPage={t('notificationLogs.pagination.rowsPerPage')}
      labelDisplayedRows={({ from, to, count }) =>
        count !== -1
          ? t('notificationLogs.pagination.displayedRows', { from, to, count })
          : t('notificationLogs.pagination.displayedRowsMoreThan', { from, to })
      }
      sx={{ color: fontColorDarkTheme, borderTop: `1px solid ${borderColorDarkTheme}`, mt: { xs: 2, md: 0 } }}
    />
  );
};

export default NotificationLogsList;